import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useDrivers } from '../../hooks/useDrivers';
import { useVehicles } from '../../hooks/useVehicles';
import { useAlertsContext } from '../../context/AlertsContext';
import { ScoreGauge } from '../../components/charts/Charts';
import { AlertCard } from '../../components/alerts/AlertCard';
import { TripHistory } from '../../components/trips/TripHistory';
import { TripDetailModal } from '../../components/trips/TripDetailModal';
import { TripMomentModal } from '../../components/trips/TripMomentModal';
import { ReportNoveltyFAB, ReportNoveltyModal } from '../../components/operator/ReportNoveltyModal';
import { PanicButton } from '../../components/operator/PanicButton';
import { PanicStatusPanel } from '../../components/operator/PanicStatusPanel';
import { usePanicAlert } from '../../hooks/usePanicAlert';
import { useTrips } from '../../hooks/useTrips';
import { mensajeErrorViaje, rangoDeFiltro, type TripDateFilter } from '../../utils/trips';
import type { Trip } from '../../types/trip';
import { RISK_LABELS, RISK_COLORS } from '../../utils/constants';
import { TrendingUp, TrendingDown, Minus, Star, AlertTriangle, Award, Gauge } from 'lucide-react';
import { formatKm } from '../../utils/formatters';

export default function OperatorPage() {
  const { user } = useAuth();
  const { drivers } = useDrivers();
  const { vehicles } = useVehicles();
  const { alerts, upsertAlert } = useAlertsContext();
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [noveltyOpen, setNoveltyOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState<TripDateFilter>('month');
  const [accionEnCurso, setAccionEnCurso] = useState<string | null>(null);
  const [avisoViaje, setAvisoViaje] = useState<string | null>(null);
  const [momento, setMomento] = useState<{ trip: Trip; accion: 'iniciar' | 'finalizar' } | null>(null);
  const [errorMomento, setErrorMomento] = useState<string | null>(null);

  useEffect(() => { document.title = 'Mi Desempeño | Paitron Radar'; }, []);

  // FE-04: el `Driver` real trae `userId` (el usuario Operador vinculado) —
  // es la forma correcta de encontrar "mi" conductor, no adivinar por nombre.
  const driver = drivers.find(d => d.userId === user?.id) ?? drivers[0];

  // FE-11: el histórico ya sale de `GET /trips` acotado a este conductor. El
  // backend además solo le deja ver los suyos al Operador, así que este
  // `driverId` es precisión de consulta, no una barrera de seguridad.
  const filtroViajes = useMemo(
    () => ({ ...rangoDeFiltro(dateFilter), driverId: driver?.id }),
    [dateFilter, driver?.id],
  );
  const { trips, isLoading: cargandoViajes, error: errorViajes, iniciar, finalizar } = useTrips(filtroViajes);
  // El viaje seleccionado se lee de la lista VIVA: tras iniciarlo o
  // finalizarlo, el modal abierto tiene que reflejar la ventana nueva.
  const selectedTrip = trips.find(t => t.id === selectedTripId) ?? null;

  // Pide el instante al iniciar/finalizar (por defecto "ahora"), para poder
  // declarar una hora pasada y registrar un viaje ya realizado (#190).
  const pedirMomento = (trip: Trip, accion: 'iniciar' | 'finalizar') => {
    setAvisoViaje(null);
    setErrorMomento(null);
    setMomento({ trip, accion });
  };
  const confirmarMomento = async (at?: string) => {
    if (!momento) return;
    const { trip, accion } = momento;
    setAccionEnCurso(trip.id);
    setErrorMomento(null);
    try {
      await (accion === 'iniciar' ? iniciar(trip.id, at) : finalizar(trip.id, at));
      setMomento(null);
    } catch (err) {
      setErrorMomento(mensajeErrorViaje(err, accion));
    } finally {
      setAccionEnCurso(null);
    }
  };
  const myAlerts = driver ? alerts.filter(a => a.driverId === driver.id) : [];
  const assignedVehicle = driver ? vehicles.find(v => v.driverId === driver.id) : undefined;
  const color = driver?.score ? RISK_COLORS[driver.score.level] : 'var(--text-muted)';

  // FE-17 — guarda DOBLE, no solo por rol. Esta ruta la abren también
  // supervisor y admin (AppRouter), y arriba `driver` cae a `drivers[0]` si no
  // hay match: sin la segunda condición, un administrador curioseando tendría
  // a mano un pánico de un gesto sobre el vehículo de otra persona.
  const esMiPerfil = user?.role === 'operator' && !!driver && driver.userId === user.id;
  const panic = usePanicAlert(assignedVehicle?.id, upsertAlert);

  const TrendIcon = driver?.score?.trend === 'up'
    ? TrendingUp : driver?.score?.trend === 'down' ? TrendingDown : Minus;

  const trendLabel = driver?.score ? {
    up: 'Mejorando — sigue así',
    down: 'En declive — necesita atención',
    stable: 'Estable',
  }[driver.score.trend] : 'Score pendiente de cálculo';

  if (!driver) {
    return (
      <div className="operator-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', color: 'var(--text-muted)' }}>
        Cargando tu perfil de conductor...
      </div>
    );
  }

  return (
    <div className="operator-page">
      <div className="operator-page__layout">
        {/* Main Info Column */}
        <div className="operator-page__main-col">
          {/* Hero score section */}
          <div className="operator-page__hero">
            <div className="operator-page__hero-left">
              <div className="operator-page__avatar">{driver.name.charAt(0)}</div>
              <div>
                <h2 className="operator-page__name">{driver.name}</h2>
                <p className="operator-page__vehicle">
                  {assignedVehicle
                    ? `Vehículo: ${assignedVehicle.plate}`
                    : 'Sin vehículo asignado'}
                </p>
                <p className="operator-page__unit">Sub-unidad: {driver.subUnitName}</p>
              </div>
            </div>

            <div className="operator-page__score-block">
              {driver.score ? (
                <>
                  <ScoreGauge score={driver.score.value} size={140} />
                  <div className="operator-page__score-meta">
                    <span
                      className="operator-page__risk-badge"
                      style={{ color, background: `${color}18`, borderColor: `${color}44` }}
                    >
                      {RISK_LABELS[driver.score.level]} riesgo
                    </span>
                    <div className="operator-page__trend" style={{ color }}>
                      <TrendIcon size={16} />
                      <span>{trendLabel}</span>
                    </div>
                    {driver.score.rankPosition !== undefined && driver.score.rankTotal !== undefined && (
                      <p className="operator-page__unit">Puesto {driver.score.rankPosition} de {driver.score.rankTotal} en tu sub-unidad</p>
                    )}
                  </div>
                </>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{trendLabel}</span>
              )}
            </div>
          </div>

          {/* Stats row — FE-04: los tres contadores salen de las alertas reales
              del conductor (mismo origen que myAlerts).
              FE-19: el cuarto ya tiene fuente — el odómetro del vehículo asignado
              (BE-50). Cambió de etiqueta a propósito: «Kilómetros recorridos»
              prometía un PERIODO, como los otros tres, y esto es el acumulado
              del EQUIPO desde que existe. Con la etiqueta vieja los cuatro
              números de esta fila no serían comparables entre sí. */}
          <div className="operator-page__stats">
            <div className="operator-page__stat">
              <AlertTriangle size={18} style={{ color: '#EF4444' }} />
              <span className="operator-page__stat-value">{myAlerts.filter(a => a.severity === 'critical').length}</span>
              <span className="operator-page__stat-label">Eventos críticos</span>
            </div>
            <div className="operator-page__stat">
              <AlertTriangle size={18} style={{ color: '#F59E0B' }} />
              <span className="operator-page__stat-value">{myAlerts.filter(a => a.severity === 'medium').length}</span>
              <span className="operator-page__stat-label">Eventos medios</span>
            </div>
            <div className="operator-page__stat">
              <Star size={18} style={{ color: '#10B981' }} />
              <span className="operator-page__stat-value">{myAlerts.filter(a => a.severity === 'low').length}</span>
              <span className="operator-page__stat-label">Eventos leves</span>
            </div>
            <div
              className="operator-page__stat"
              title="Es el acumulado del equipo GPS, no lo recorrido en un periodo."
            >
              <Gauge size={18} style={{ color: '#3B82F6' }} />
              <span className="operator-page__stat-value">
                {assignedVehicle?.odometer ? formatKm(assignedVehicle.odometer.km) : '—'}
              </span>
              <span className="operator-page__stat-label">Odómetro del vehículo</span>
            </div>
          </div>

          {/* Feedback message */}
          {driver.score ? (
            <div className="operator-page__feedback" style={{ borderColor: `${color}44`, background: `${color}0A` }}>
              {driver.score.level === 'low' && (
                <>
                  <Award size={20} style={{ color }} />
                  <p>¡Excelente desempeño! Mantén estas prácticas de conducción segura.</p>
                </>
              )}
              {driver.score.level === 'medium' && (
                <>
                  <TrendingUp size={20} style={{ color }} />
                  <p>Tu conducción puede mejorar. Presta atención a los eventos registrados para subir tu score.</p>
                </>
              )}
              {driver.score.level === 'high' && (
                <>
                  <AlertTriangle size={20} style={{ color }} />
                  <p><strong>Alerta de desempeño:</strong> Tu nivel de riesgo es alto. Por favor, revisa tus eventos recientes y contacta a tu supervisor.</p>
                </>
              )}
            </div>
          ) : (
            <div className="operator-page__feedback" style={{ borderColor: 'var(--border)', background: 'var(--bg-raised)' }}>
              <p>Tu score de conducción todavía no está disponible — revisa tus eventos recientes mientras tanto.</p>
            </div>
          )}

          {/* Recent events feed — la lista va en su propio scroll (igual que
              TripHistory) porque un conductor con muchas alertas puede tener
              decenas: sin tope, empujaba el Histórico de Recorridos varias
              pantallas hacia abajo en mobile. */}
          {myAlerts.length > 0 && (
            <div className="operator-page__events">
              <h3 className="operator-page__events-title">Mis eventos recientes</h3>
              <div className="operator-page__events-list">
                {myAlerts.map(alert => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Trip History Column */}
        <div className="operator-page__map-col">
          {avisoViaje && (
            <p style={{ margin: '0 0 0.75rem', padding: '0.625rem 0.875rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)', color: '#EF4444', fontSize: '12px', fontWeight: 600 }}>
              {avisoViaje}
            </p>
          )}
          <TripHistory
            trips={trips}
            isLoading={cargandoViajes}
            error={errorViajes}
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
            onSelectTrip={trip => setSelectedTripId(trip.id)}
            role={user?.role}
            miDriverId={esMiPerfil ? driver.id : undefined}
            onIniciar={trip => pedirMomento(trip, 'iniciar')}
            onFinalizar={trip => pedirMomento(trip, 'finalizar')}
            accionEnCurso={accionEnCurso}
          />
        </div>
      </div>

      {/* Trip Detail Modal */}
      <TripDetailModal
        isOpen={selectedTrip !== null}
        onClose={() => setSelectedTripId(null)}
        trip={selectedTrip}
        role={user?.role}
        miDriverId={esMiPerfil ? driver.id : undefined}
        onIniciar={trip => pedirMomento(trip, 'iniciar')}
        onFinalizar={trip => pedirMomento(trip, 'finalizar')}
      />

      <TripMomentModal
        isOpen={momento !== null}
        accion={momento?.accion ?? 'iniciar'}
        trip={momento?.trip ?? null}
        isLoading={accionEnCurso === momento?.trip.id}
        error={errorMomento}
        canBackdate={user?.role === 'admin' || user?.role === 'supervisor'}
        onConfirm={confirmarMomento}
        onClose={() => { setMomento(null); setErrorMomento(null); }}
      />

      {/* FE-17: el pánico va a la IZQUIERDA. La esquina derecha ya apila el
          chat y "Reportar Novedad" — y una emergencia pegada a un informe de
          novedad es el mis-tap más caro posible. Son dos superficies distintas
          y no se fusionan. */}
      {esMiPerfil && (
        <>
          <PanicButton
            onConfirm={panic.enviar}
            disabled={!assignedVehicle}
            busy={panic.phase === 'sending'}
          />
          {/* Sin vehículo asignado el botón se deshabilita con explicación, no
              se esconde: que desaparezca sin decir nada es peor. */}
          {!assignedVehicle && (
            <p className="panic-fab__aviso">Sin vehículo asignado hoy: no puedes declarar una emergencia desde la app.</p>
          )}
          <PanicStatusPanel
            phase={panic.phase}
            intento={panic.intento}
            maxIntentos={panic.maxIntentos}
            alert={panic.alert}
            fallo={panic.fallo}
            enviar={panic.enviar}
            descartar={panic.descartar}
            plate={assignedVehicle?.plate ?? ''}
          />
        </>
      )}

      {/* Report Novelty FAB + Modal */}
      <ReportNoveltyFAB onClick={() => setNoveltyOpen(true)} />
      {/* FE-13 — el vehículo asignado viaja en el cuerpo porque el servidor
          no lo adivina; el conductor NO, porque el servidor lo estampa solo
          (§4.8) y mandarlo sería una sugerencia que igual ignoraría. */}
      <ReportNoveltyModal
        isOpen={noveltyOpen}
        onClose={() => setNoveltyOpen(false)}
        vehicle={assignedVehicle}
      />
    </div>
  );
}
